/**
 * /tools/hero-vision?clientId=X — STP-032.
 *
 * Step 3 long-form vision: 3 horizons (1y / 3y / 10y) × 3 domains
 * (energy / work / love) + optional north-star sentence.
 *
 * Persists via the generic tools route → versioned `client_documents`
 * row w/ `doc_type='hero_vision'` (R1.3 sub-step 5 persist).
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/app/auth-context';
import * as api from '@/lib/api-client';
import { useClientId } from '@/lib/use-client-id';
import { ApiError } from '@/lib/api-client';
import { FinalizeWarning } from '@/components/finalize-warning';
import { PrintButton } from '@/components/print-button';

const HORIZONS = ['oneYear', 'threeYear', 'tenYear'] as const;
const DOMAINS = ['energy', 'work', 'love'] as const;
type Horizon = (typeof HORIZONS)[number];
type Domain = (typeof DOMAINS)[number];

type Cells = Record<Horizon, Record<Domain, string>>;
const EMPTY: Cells = {
  oneYear: { energy: '', work: '', love: '' },
  threeYear: { energy: '', work: '', love: '' },
  tenYear: { energy: '', work: '', love: '' },
};

/** Stable snapshot of the form — drives the "saved & unchanged → disable" state. */
function snapshot(cells: Cells, northStar: string): string {
  return JSON.stringify({ ...cells, northStar });
}

export function HeroVisionPage(): JSX.Element {
  const { t } = useTranslation();
  const { accessToken, user } = useAuth();
  const clientId = useClientId();

  const [cells, setCells] = useState<Cells>(EMPTY);
  const [northStar, setNorthStar] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [savedSnapshot, setSavedSnapshot] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    let active = true;
    if (!clientId) {
      setHydrated(true);
      return;
    }
    void (async () => {
      try {
        const existing = await api.getToolCompletion(accessToken, clientId, 'hero-vision');
        if (!active) return;
        if (existing?.payload && typeof existing.payload === 'object') {
          const p = existing.payload as Record<string, unknown>;
          const next: Cells = { oneYear: { ...EMPTY.oneYear }, threeYear: { ...EMPTY.threeYear }, tenYear: { ...EMPTY.tenYear } };
          HORIZONS.forEach((h) => {
            const hp = p[h];
            if (hp && typeof hp === 'object') {
              const obj = hp as Record<string, unknown>;
              DOMAINS.forEach((d) => {
                if (typeof obj[d] === 'string') next[h][d] = obj[d];
              });
            }
          });
          const loadedNorthStar = typeof p.northStar === 'string' ? p.northStar : '';
          setNorthStar(loadedNorthStar);
          setCells(next);
          setSavedAt(existing.completed_at ?? null);
          setSavedSnapshot(snapshot(next, loadedNorthStar));
        }
      } catch {
        /* swallow */
      } finally {
        if (active) setHydrated(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [accessToken, clientId]);

  const allFilled = useMemo(
    () => HORIZONS.every((h) => DOMAINS.every((d) => cells[h][d].trim().length > 0)),
    [cells],
  );
  const currentSnapshot = useMemo(() => snapshot(cells, northStar), [cells, northStar]);
  // Saved and unchanged → the button locks (the standard tool behavior from
  // the earlier steps), re-enabling only once the client edits something.
  const isSavedClean = savedAt !== null && savedSnapshot === currentSnapshot;
  const canSubmit = !submitting && hydrated && allFilled;

  const updateCell = (h: Horizon, d: Domain, v: string): void => {
    setCells((prev) => ({ ...prev, [h]: { ...prev[h], [d]: v } }));
  };

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setSubmitError(null);
    if (!user) {
      setSubmitError(t('tools.heroVision.errors.notAuthenticated'));
      return;
    }
    if (!clientId) {
      setSubmitError(t('tools.heroVision.errors.missingClient'));
      return;
    }
    if (!allFilled) {
      setSubmitError(t('tools.heroVision.errors.incomplete'));
      return;
    }
    setSubmitting(true);
    const buildHorizon = (h: Horizon): { energy: string; work: string; love: string } => ({
      energy: cells[h].energy.trim(),
      work: cells[h].work.trim(),
      love: cells[h].love.trim(),
    });
    const payload = {
      oneYear: buildHorizon('oneYear'),
      threeYear: buildHorizon('threeYear'),
      tenYear: buildHorizon('tenYear'),
      ...(northStar.trim() ? { northStar: northStar.trim() } : {}),
    };
    try {
      const completion = await api.submitToolCompletion(
        accessToken,
        clientId,
        'hero-vision',
        { payload },
      );
      setSavedAt(completion.completed_at ?? new Date().toISOString());
      setSavedSnapshot(snapshot(cells, northStar));
    } catch (cause) {
      if (cause instanceof ApiError) {
        setSubmitError(cause.envelope.message || t('tools.heroVision.errors.generic'));
      } else {
        setSubmitError(t('tools.heroVision.errors.generic'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!clientId) {
    return (
      <section className="max-w-2xl mx-auto px-s5 py-s8" data-testid="hero-vision-missing-client">
        <h1 className="font-display text-display-lg text-ink">{t('tools.heroVision.title')}</h1>
        <p className="mt-s3 text-flame-ember">{t('tools.heroVision.errors.missingClient')}</p>
      </section>
    );
  }

  return (
    <section className="max-w-4xl mx-auto px-s5 py-s7">
      <div className="flex justify-end mb-s2"><PrintButton /></div>
      <header className="mb-s6">
        <p className="font-mono text-xs uppercase tracking-wide text-gray-1">
          {t('tools.heroVision.eyebrow')}
        </p>
        <h1 className="font-display text-display-xl text-ink mt-s2">
          {t('tools.heroVision.title')}
        </h1>
        <p className="mt-s3 text-gray-2 max-w-prose">{t('tools.heroVision.description')}</p>
      </header>

      <form
        onSubmit={(event) => {
          void onSubmit(event);
        }}
        className="space-y-s5"
        noValidate
      >
        <article className="rounded-lg border border-bone-3 bg-bone-2 p-s5">
          <label htmlFor="hero-vision-northStar" className="block">
            <h2 className="font-display text-display-sm text-ink">
              {t('tools.heroVision.fields.northStar.title')}
              <span className="ml-s2 text-xs font-normal uppercase tracking-wide text-gray-1">
                {t('tools.heroVision.optional')}
              </span>
            </h2>
            <p className="mt-s2 text-gray-2 italic">
              {t('tools.heroVision.fields.northStar.prompt')}
            </p>
          </label>
          <textarea
            id="hero-vision-northStar"
            value={northStar}
            onChange={(event) => setNorthStar(event.target.value)}
            placeholder={t('tools.heroVision.fields.northStar.placeholder')}
            className="input-field mt-s3 min-h-20"
            data-testid="hero-vision-northStar"
            rows={3}
          />
        </article>

        {HORIZONS.map((h) => (
          <article
            key={h}
            className="rounded-lg border border-bone-3 bg-bone-2 p-s5"
            data-testid={`hero-vision-${h}`}
          >
            <h2 className="font-display text-display-sm text-ink">
              {t(`tools.heroVision.horizons.${h}`)}
            </h2>
            <div className="mt-s3 grid grid-cols-1 md:grid-cols-3 gap-s3">
              {DOMAINS.map((d) => (
                <div key={d}>
                  <label
                    htmlFor={`hero-vision-${h}-${d}`}
                    className="form-label"
                  >
                    {t(`tools.heroVision.domains.${d}`)}
                  </label>
                  <textarea
                    id={`hero-vision-${h}-${d}`}
                    value={cells[h][d]}
                    onChange={(event) => updateCell(h, d, event.target.value)}
                    placeholder={t(`tools.heroVision.fields.${d}Placeholder`)}
                    className="input-field mt-s2 min-h-32"
                    data-testid={`hero-vision-${h}-${d}`}
                    rows={5}
                  />
                </div>
              ))}
            </div>
          </article>
        ))}

        {submitError && (
          <p role="alert" className="form-error" data-testid="hero-vision-submit-error">
            {submitError}
          </p>
        )}

        {savedAt && (
          <p role="status" className="text-sm text-flame-deep" data-testid="hero-vision-saved-at">
            ✓ {t('tools.heroVision.savedAt', { when: savedAt.slice(0, 19).replace('T', ' ') })}
          </p>
        )}

        <button
          type="submit"
          className="btn-primary w-full"
          disabled={!canSubmit || isSavedClean}
          data-testid="hero-vision-submit"
        >
          {submitting
            ? t('tools.heroVision.submitting')
            : isSavedClean
              ? t('tools.heroVision.savedButton')
              : t('tools.heroVision.submit')}
        </button>
        <FinalizeWarning />
      </form>
    </section>
  );
}
